<template>
    <div />
</template>

<script>
import { mapState } from 'vuex';
import CourseSettings from '@/models/CourseSettings.js';
import axios from '@/plugins/axios.js';

export default {
    name: 'CourseKeyIntegrationListener',

    data() {
        return {
            pollingInterval: null,
            initialPollingTimeout: null,
            requiresConfirmedOutcome: false,
            pollingGeneration: 0,
            pollingStartTime: null,
            maxPollingDuration: 5 * 60 * 1000, // 5 minutes in milliseconds
        };
    },

    computed: {
        ...mapState('Auth', { authUser: state => state.user }),
        ...mapState('CourseTemplateForm', ['form']),

        courseId() {
            return this.form?.course?.id;
        },

        isEditingCourse() {
            return this.courseId && this.$route.name === 'course-edit-settings';
        },
    },

    watch: {
        courseId: {
            immediate: true,
            handler(newCourseId, oldCourseId) {
                if (oldCourseId && oldCourseId !== newCourseId) {
                    this.removeListener(oldCourseId);
                    this.stopPolling();
                    this.requiresConfirmedOutcome = false;
                }

                if (newCourseId && this.isEditingCourse) {
                    this.setupListener(newCourseId);
                }
            },
        },
    },

    beforeUnmount() {
        if (this.courseId) {
            this.removeListener(this.courseId);
        }
        this.stopPolling();
    },

    methods: {
        setupListener(courseId) {
            this.$echo
                .private(`course-key-integration-${courseId}`)
                .listen('CourseKeyIntegrationStarted', this.handleStarted)
                .listen('CourseKeyIntegrationCompleted', this.handleCompleted)
                .listen('CourseKeyIntegrationFailed', this.handleFailed)
                .error(error => {
                    console.error('CourseKey integration websocket error:', error);
                });
        },

        removeListener(courseId) {
            const channel = this.$echo.private(`course-key-integration-${courseId}`);
            channel.stopListening('CourseKeyIntegrationStarted');
            channel.stopListening('CourseKeyIntegrationCompleted');
            channel.stopListening('CourseKeyIntegrationFailed');
        },

        handleStarted(payload) {
            this.requiresConfirmedOutcome = payload?.requires_confirmed_outcome === true;
            this.$store.commit('CourseTemplateForm/SET_COURSE_KEY_INTEGRATION_IN_PROGRESS', true);

            // Keep autosaver in "Saving..." state
            this.$store.commit('AutoSave/SET_SAVING', {
                indicator: 'CourseTemplateForm/courseKeyIntegration',
                message: 'Saving...',
            });

            // Start polling fallback in case websocket fails
            this.startPolling();
        },

        handleCompleted(payload) {
            const syncResult = payload?.course_key_sync;
            const incomplete =
                (this.requiresConfirmedOutcome && !syncResult) ||
                (syncResult && !['synced', 'not_required'].includes(syncResult.status));
            const localSaved = syncResult?.local_saved === true || (this.requiresConfirmedOutcome && !syncResult);
            const message =
                syncResult?.message ||
                (incomplete
                    ? 'CourseKey synchronization was not confirmed. Review the course in CourseKey Admin Station.'
                    : 'CourseKey integration completed successfully');
            let savedMessage = 'Saved';
            let toastType = 'is-success';
            if (incomplete) {
                savedMessage = localSaved ? 'Saved in Edverum' : 'Synchronization incomplete';
                toastType = syncResult?.status === 'failed' ? 'is-danger' : 'is-warning';
            }
            this.stopPolling();
            this.requiresConfirmedOutcome = false;
            this.$store.commit('CourseTemplateForm/SET_COURSE_KEY_INTEGRATION_IN_PROGRESS', false);
            this.$store.commit('CourseTemplateForm/SET_COURSE_KEY_INTEGRATION_ERROR', incomplete ? message : null);
            this.$store.commit('AutoSave/SET_SAVED', {
                indicator: 'CourseTemplateForm/courseKeyIntegration',
                message: savedMessage,
            });
            this.$edverumToast.open({
                message,
                type: toastType,
                duration: incomplete ? 10000 : 5000,
            });
        },

        handleUnconfirmedOutcome() {
            this.stopPolling();
            this.requiresConfirmedOutcome = false;
            const message =
                'Saved in Edverum. CourseKey synchronization has not been confirmed. Refresh this page or review the course in CourseKey Admin Station before retrying.';
            this.$store.commit('CourseTemplateForm/SET_COURSE_KEY_INTEGRATION_IN_PROGRESS', false);
            this.$store.commit('CourseTemplateForm/SET_COURSE_KEY_INTEGRATION_ERROR', message);
            this.$store.commit('AutoSave/SET_SAVED', {
                indicator: 'CourseTemplateForm/courseKeyIntegration',
                message: 'Saved in Edverum · synchronization unconfirmed',
            });
            this.$edverumToast.open({ message, type: 'is-warning', duration: 10000 });
        },

        handleFailed(payload) {
            this.stopPolling();
            this.requiresConfirmedOutcome = false;
            this.$store.commit('CourseTemplateForm/SET_COURSE_KEY_INTEGRATION_IN_PROGRESS', false);
            this.$store.commit('CourseTemplateForm/SET_COURSE_KEY_INTEGRATION_ERROR', payload.error);

            // Mark autosaver as saved (to clear the "Saving..." state)
            this.$store.commit('AutoSave/SET_SAVED', {
                indicator: 'CourseTemplateForm/courseKeyIntegration',
                message: 'Saved',
            });

            // A saved correction can fail to synchronize without disabling the integration.
            const courseSettings = this.form.course.course_settings;
            if (courseSettings?.id && !payload.preserve_enabled_setting) {
                CourseSettings.find(courseSettings.id).$update({ course_key_enabled: false });

                this.$store.commit('CourseTemplateForm/updateCourseField', {
                    field: 'course_settings.course_key_enabled',
                    value: false,
                });
            }

            this.$edverumToast.open({
                message: `CourseKey integration failed: ${payload.error}`,
                type: 'is-danger',
                duration: 10000,
            });
        },

        startPolling() {
            // Clear any existing polling interval
            this.stopPolling();

            // Set start time for timeout tracking
            this.pollingStartTime = Date.now();

            // Start polling every 30 seconds
            this.pollingInterval = setInterval(() => {
                this.checkIntegrationStatus();
            }, 30000); // 30 seconds

            // Also check immediately after a short delay to catch quick completions
            this.initialPollingTimeout = setTimeout(() => {
                this.checkIntegrationStatus();
            }, 2000);
        },

        stopPolling() {
            this.pollingGeneration += 1;
            if (this.initialPollingTimeout) {
                clearTimeout(this.initialPollingTimeout);
                this.initialPollingTimeout = null;
            }
            if (this.pollingInterval) {
                clearInterval(this.pollingInterval);
                this.pollingInterval = null;
            }
            this.pollingStartTime = null;
        },

        async checkIntegrationStatus() {
            // Stop polling if max duration exceeded
            if (this.pollingStartTime !== null && Date.now() - this.pollingStartTime >= this.maxPollingDuration) {
                if (this.requiresConfirmedOutcome) {
                    this.handleUnconfirmedOutcome();
                    return;
                }
                this.stopPolling();
                console.warn('CourseKey integration polling timeout reached. Please refresh the page to check status.');
                return;
            }

            // Only poll if integration is still in progress
            if (!this.$store.state.CourseTemplateForm.courseKeyIntegrationInProgress) {
                this.stopPolling();
                return;
            }

            if (!this.courseId || this.requiresConfirmedOutcome) {
                // Existing provider identifiers cannot prove that a correction synchronized.
                return;
            }

            const generation = this.pollingGeneration;
            try {
                const { data } = await axios.get(`/api/courses/${this.courseId}?basic-information`, {
                    errorHandle: false,
                });

                if (generation !== this.pollingGeneration || this.requiresConfirmedOutcome) return;
                const course = data?.data;

                // Check if integration completed successfully
                // Integration is complete when both course_key_id and course_key_add_code are set
                if (course?.course_key_id && course?.course_key_add_code) {
                    this.handleCompleted({
                        course_id: this.courseId,
                        model_type: 'course',
                        model_id: this.courseId,
                        status: 'completed',
                    });
                }
            } catch (error) {
                // Silently handle errors - don't spam console if API fails
                // We'll retry on next polling interval
                console.warn('CourseKey integration status check failed:', error);
            }
        },
    },
};
</script>
